import type { ZonePayload } from '../../net/protocol';

import { isSolid, tileAt, tileCentre, walkableTiles } from './layout';
import { cellHash } from './town';

/**
 * Rubble along the foot of a wall.
 *
 * ── What it is for ──────────────────────────────────────────────────────────
 *
 * A combat zone is boxes and a floor. `buildTown` returns early outside the hub, so
 * every shopfront, door, window, beam, chimney and lamp post in the game is town-only —
 * which is why a dungeon reads as *uniform blocks with nowhere for the eye to rest*
 * (owner's report, 2026-08-11) in a way the town does not.
 *
 * The cheapest honest answer is not to give a crypt windows. It is to break the line
 * where a wall meets the floor, which is the same edge the vertex AO darkens: a wall that
 * stops dead on flat ground reads as a prop, and one with a scatter of fallen stone at
 * its foot reads as masonry.
 *
 * ── Why it is generated rather than modelled ────────────────────────────────
 *
 * Zero bytes. These are boxes placed from a hash of their own tile, merged into the
 * structure mesh they belong to, so the whole scatter costs one more entry in a vertex
 * buffer that was already being built — no download, no draw call, no material.
 *
 * **Deterministic, and that is load-bearing.** Zone geometry is rebuilt from the ZONE
 * frame every time a player arrives, so anything drawn from an RNG would rearrange the
 * rubble on every visit and show two players in one zone different debris. Same rule the
 * packs and `Step` follow: seeded state, never a global source.
 */
export interface Debris {
  /** World position of the piece's centre. */
  at: { x: number; z: number };
  /** Cube side, world units. */
  size: number;
  /** Yaw, so a field of cubes does not read as a grid. */
  yaw: number;
}

/**
 * How much of a tile a piece of rubble may be, **as a fraction of `tileSize`**.
 *
 * The first version had these as absolute world units at the same numbers, with a comment
 * calling them a fraction of a tile — so every piece came out 0.08–0.2 units, which is
 * two to five device pixels at the gameplay camera and invisible on screen. Nothing
 * failed: the scatter was placed, merged and drawn exactly as written. Caught by looking
 * at it, which is the only thing that could have caught it.
 *
 * The floor to clear is `MIN_OUTLINE_WIDTH` (≈0.12 units): below it a thing does not
 * reliably occupy a whole pixel once the pixelate pass is on, and that pass is still an
 * option. At a 2.4-unit tile these give 0.24–0.53 units, comfortably above it.
 *
 * Small at the top end too, and deliberately: this is fallen stone at the foot of a wall,
 * not an obstacle. Anything a player could mistake for cover is a lie, because none of it
 * is on the server's walkability grid — the server knows tiles, and a prop blocking a path
 * the server allows is the client drawing a world the server disagrees with.
 */
export const MIN_DEBRIS_FRACTION = 0.1;
export const MAX_DEBRIS_FRACTION = 0.22;

/** How many of the eligible tiles get a piece, as a fraction. */
export const DEBRIS_DENSITY = 0.45;

/**
 * Where rubble falls: walkable tiles that touch a solid one.
 *
 * Only against a wall, deliberately. Scattered across open floor it reads as litter and
 * fights the cobbles for attention; banked against the thing it fell off, it reads as
 * decay and does the job the flat contact line was failing to do.
 *
 * `salt` separates this hash stream from the one that tints buildings, so a change to
 * one does not silently rearrange the other.
 */
/**
 * Whether the scatter is drawn at all. **Off**, on the owner's instruction.
 *
 * Reported: *stone blocks that you can go through is a bit weird, we should remove them for now,
 * especially the one inside the town portal in town.*
 *
 * The docstring above had already named this as the risk — *anything a player could mistake for
 * cover is a lie, because none of it is on the server's walkability grid* — and the report is that
 * prediction arriving. The pieces are 0.24–0.53 units of solid-looking masonry that a character
 * walks straight through, and one of them lands next to the town portal, which is the single most
 * trafficked square in the game.
 *
 * **Off rather than deleted, and the generator is still tested.** What is wrong here is not the
 * placement — rubble banked against a wall is exactly the right answer to a dungeon of uniform
 * blocks — it is that this game has no way to make a prop solid. Tiles are the server's unit of
 * walkability, and a sub-tile obstacle cannot be expressed on that grid at all. So the arithmetic
 * stays, with its coverage, and comes back the day there is either a collision system for props or
 * a decision that decoration may overlap a character.
 *
 * `debrisAt` is the seam the tests use, so turning this off does not turn off the tests that
 * describe what it would place.
 */
export const DEBRIS_ENABLED = false;

/** What the zone renderer draws: the scatter, or nothing while it is disabled. */
export function debrisFor(zone: ZonePayload, salt = 977): Debris[] {
  return DEBRIS_ENABLED ? debrisAt(zone, salt) : [];
}

/**
 * The scatter itself, independent of whether anything draws it.
 *
 * Separated from `debrisFor` so `DEBRIS_ENABLED` is one line rather than a change that also
 * deletes every assertion about where rubble goes and how big it is.
 */
export function debrisAt(zone: ZonePayload, salt = 977): Debris[] {
  const out: Debris[] = [];

  for (const tile of walkableTiles(zone)) {
    // **`isSolid`, not `exposedEdges`.** The first version of this used the latter on the
    // reasoning that it answers "which of my neighbours are wall" from the other side. It
    // does not: it answers which are **VOID** — off the map — because its job is deciding
    // where to build a wall, and a structure needs none since it fills its own tile. A
    // walkable tile beside a building therefore has no exposed edge at all, and the
    // scatter came out empty. Caught by the test below asserting there is any rubble.
    const againstWall =
      isSolid(tileAt(zone, tile.col, tile.row - 1).kind) ||
      isSolid(tileAt(zone, tile.col, tile.row + 1).kind) ||
      isSolid(tileAt(zone, tile.col + 1, tile.row).kind) ||
      isSolid(tileAt(zone, tile.col - 1, tile.row).kind);
    if (!againstWall) continue;

    const hash = cellHash(tile.col * salt, tile.row * salt);
    if (hash > DEBRIS_DENSITY) continue;

    // Re-hashed off the first, so position, size and yaw do not move together — a scatter
    // whose big pieces are always in the same corner of their tile reads as a pattern.
    const place = cellHash(tile.row * salt, tile.col * salt);
    const centre = tileCentre(zone, tile.col, tile.row);
    const spread = zone.tileSize * 0.3;

    out.push({
      at: {
        x: centre.x + (place - 0.5) * spread,
        z: centre.z + (hash / DEBRIS_DENSITY - 0.5) * spread,
      },
      size: zone.tileSize * (MIN_DEBRIS_FRACTION + place * (MAX_DEBRIS_FRACTION - MIN_DEBRIS_FRACTION)),
      yaw: place * Math.PI,
    });
  }

  return out;
}
